/**
 * <sliding-chips> — the blog listing's sliding chip fill, for any row of [aria-pressed] chips
 * (faq-page, store-locator). Styles: snippets/ambera-sliding-chips.liquid.
 *
 * The element is the chip row itself. It never decides which chip is pressed: the page script sets
 * aria-pressed as before, and this element follows.
 *   - the active chip's dark fill is one element (.sliding-chips__indicator) that slides to the chip
 *     just pressed, crossing every chip in between
 *   - a copy of the chips in their active colours (.sliding-chips__overlay) is clipped to the same
 *     moving pill, so labels turn white exactly where the fill is
 *   - with no chip pressed (store locator search) the fill steps aside; it comes back without motion
 *   - it is re-placed without motion whenever the row reflows, and once the web fonts are in
 *
 * While the fill is placed the row carries .is-animated, and the section restyles its pressed chip
 * in the resting look (the fill and the overlay draw the active one). Before the script runs the
 * pressed chip keeps painting itself, so nothing is lost without JavaScript.
 *
 * Markup contract: <sliding-chips> > button[aria-pressed] (any number, any content)
 */
class SlidingChips extends HTMLElement {
  /** @type {HTMLElement | null} */
  #indicator = null;
  /** @type {HTMLElement | null} */
  #overlay = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {MutationObserver | null} */
  #pressObserver = null;
  /** @type {HTMLElement | null} The chip the fill sits under (or is on its way to). */
  #current = null;

  connectedCallback() {
    this.#indicator = document.createElement('span');
    this.#indicator.className = 'sliding-chips__indicator';
    this.#indicator.setAttribute('aria-hidden', 'true');
    this.#overlay = this.#buildOverlay();
    this.prepend(this.#indicator);
    this.append(this.#overlay);

    this.#place(false);
    this.#resizeObserver = new ResizeObserver(() => this.#place(false));
    this.#resizeObserver.observe(this);
    document.fonts?.ready.then(() => this.#place(false));

    // One callback per batch of aria-pressed changes, after the page script has set them all.
    // Scripts may re-set the same chip (store-locator does after its fade): that is not a move.
    this.#pressObserver = new MutationObserver(() => {
      if (this.#pressed === this.#current) return;
      this.#place(this.classList.contains('is-animated'));
    });
    this.#pressObserver.observe(this, { attributes: true, attributeFilter: ['aria-pressed'], subtree: true });
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#pressObserver?.disconnect();
    this.#indicator?.remove();
    this.#overlay?.remove();
    this.classList.remove('is-animated', 'is-ready');
  }

  get #pressed() {
    return this.#chips.find((element) => element.getAttribute('aria-pressed') === 'true') ?? null;
  }

  get #chips() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll(':scope > [aria-pressed]')]);
  }

  /**
   * Put the fill under the pressed chip. Without `animate` it jumps there: transitions are off
   * while the new position is applied and flushed, then back on.
   * @param {boolean} animate
   */
  #place(animate) {
    const indicator = this.#indicator;
    const overlay = this.#overlay;
    if (!indicator || !overlay) return;

    const chip = this.#pressed;
    this.#current = chip;
    if (!chip) {
      this.classList.remove('is-animated', 'is-ready');
      return;
    }

    if (!animate) this.classList.remove('is-ready');

    // The overlay spans the whole scrollable row (store locator chips scroll sideways on mobile);
    // it is collapsed while the row is measured so it never keeps the row wider than its chips.
    // (Not `hidden`: display: none would cut a running clip-path transition short.)
    overlay.style.width = '0px';
    overlay.style.height = '0px';
    const rowWidth = this.scrollWidth;
    const rowHeight = this.scrollHeight;
    overlay.style.width = `${rowWidth}px`;
    overlay.style.height = `${rowHeight}px`;

    const x = chip.offsetLeft;
    const y = chip.offsetTop;
    const width = chip.offsetWidth;
    const height = chip.offsetHeight;
    indicator.style.setProperty('--indicator-x', `${x}px`);
    indicator.style.setProperty('--indicator-y', `${y}px`);
    indicator.style.setProperty('--indicator-w', `${width}px`);
    indicator.style.setProperty('--indicator-h', `${height}px`);

    // Same rectangle as the fill, as clip insets, so the white labels travel with it
    overlay.style.setProperty('--clip-t', `${y}px`);
    overlay.style.setProperty('--clip-l', `${x}px`);
    overlay.style.setProperty('--clip-r', `${rowWidth - x - width}px`);
    overlay.style.setProperty('--clip-b', `${rowHeight - y - height}px`);
    this.classList.add('is-animated');

    if (!animate) {
      void indicator.offsetWidth; // commit the jump before transitions come back
      this.classList.add('is-ready');
    }
  }

  /**
   * A decorative copy of the chips, laid out exactly like the real row, in their active colours.
   * The copies keep the chips' classes and content (the store locator's count pill included).
   */
  #buildOverlay() {
    const overlay = document.createElement('span');
    overlay.className = 'sliding-chips__overlay';
    overlay.setAttribute('aria-hidden', 'true');
    for (const chip of this.#chips) {
      const copy = document.createElement('span');
      copy.className = `${chip.className} sliding-chips__copy`;
      for (const child of chip.childNodes) copy.append(child.cloneNode(true));
      overlay.append(copy);
    }
    return overlay;
  }
}

if (!customElements.get('sliding-chips')) {
  customElements.define('sliding-chips', SlidingChips);
}
